import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, Clock, Users, Share2, ChevronRight, Trash2, Lock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/services/api";

// Backend OG endpoint - WhatsApp/iMessage scrapers fetch this URL to get
// proper title/image previews. Human visitors are redirected to the real
// match page server-side, so the experience is identical to sharing the
// canonical /match/:id URL.
const MATCH_SHARE_URL_BASE = "https://matchsups.com/api/og/match/";

export default function MatchCard({ match, onDeleted, onDeleteError }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const progressPercent = (match.current_players / match.max_players) * 100;
  const isFull = match.current_players >= match.max_players;
  const isCompleted = match.status === "completed";
  const isOrganizer = user?.id && match.organizer_id === user.id;
  const isPrivate = !!match.is_private;

  const handleCardClick = () => {
    if (isPrivate) {
      toast.info("Match prive — accessible via le lien d'invitation");
      return;
    }
    navigate(`/match/${match.id}`);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    const url = `${MATCH_SHARE_URL_BASE}${match.id}`;
    const message = `Je t'invite au match "${match.title}" le ${match.date} a ${match.time} - ${match.location}.\n\nRejoins-nous : ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
    toast.success("Lien WhatsApp ouvert!");
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (!confirm(`Supprimer definitivement le match "${match.title}" ?`)) return;
    // Optimistic update: the card vanishes from the list instantly (<100ms),
    // the DELETE request runs in the background. If it fails, the parent
    // re-syncs the list via onDeleteError.
    onDeleted?.(match.id);
    toast.success("Match supprime");
    api.deleteMatch(match.id).catch((err) => {
      toast.error(err.message || "Suppression refusee");
      onDeleteError?.();
    });
  };

  return (
    <div
      data-testid={`match-card-${match.id}`}
      onClick={handleCardClick}
      style={{
        backgroundColor: '#111111',
        border: '0.5px solid #333',
        borderRadius: '16px',
        padding: '20px',
        cursor: 'pointer',
        transition: 'border-color 0.2s',
      }}
      className="hover:border-[#22c55e]/50"
    >
      {/* Title Row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h3 className="font-heading" style={{ fontSize: '15px', fontWeight: 600, color: '#fff', flex: 1, paddingRight: '8px' }}>
          {match.title}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {isPrivate && (
            <Badge
              data-testid={`badge-private-${match.id}`}
              style={{
                backgroundColor: 'rgba(148,163,184,0.15)', color: '#cbd5e1',
                fontSize: '10px', fontWeight: 700, letterSpacing: '0.03em',
                border: '1px solid #475569', display: 'flex', alignItems: 'center', gap: '3px',
              }}
            >
              <Lock size={10} /> PRIVE
            </Badge>
          )}
          {isCompleted && (
            <Badge
              data-testid={`status-completed-${match.id}`}
              style={{
                backgroundColor: '#ef4444',
                color: '#fff',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.04em',
                boxShadow: '0 2px 8px rgba(239,68,68,0.35)',
                border: 'none',
              }}
            >
              TERMINE
            </Badge>
          )}
          <ChevronRight size={18} color="#4b5563" style={{ flexShrink: 0 }} />
        </div>
      </div>

      {/* Score Banner for completed matches */}
      {isCompleted && match.score_a != null && (
        <div data-testid={`match-score-${match.id}`} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
          padding: '12px', marginBottom: '16px', borderRadius: '10px',
          background: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(59,130,246,0.08))',
          border: '1px solid #222',
        }}>
          <span style={{ fontSize: '11px', color: '#22c55e', fontWeight: 600 }}>EQ.A</span>
          <span style={{ fontSize: '28px', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {match.score_a}
          </span>
          <span style={{ fontSize: '18px', color: '#6b7280' }}>-</span>
          <span style={{ fontSize: '28px', fontWeight: 700, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
            {match.score_b}
          </span>
          <span style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 600 }}>EQ.B</span>
        </div>
      )}

      {/* Info Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MapPin size={14} color="#22c55e" />
          <span style={{ fontSize: '13px', color: '#9ca3af' }}>{match.location_name || match.location}</span>
          {match.city && (
            <span data-testid={`match-city-${match.id}`} style={{
              fontSize: '11px', fontWeight: 600, color: '#22c55e',
              backgroundColor: 'rgba(34,197,94,0.1)', padding: '1px 8px', borderRadius: '9999px',
            }}>
              {match.city}
            </span>
          )}
          {(match.country === 'CH' || match.country === 'BE') && (
            <span data-testid={`match-flag-${match.id}`} title={match.country === 'CH' ? 'Suisse' : 'Belgique'} style={{ fontSize: '12px', lineHeight: 1 }}>
              {match.country === 'CH' ? '🇨🇭' : '🇧🇪'}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={14} color="#22c55e" />
            <span style={{ fontSize: '13px', color: '#9ca3af' }}>{match.date}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={14} color="#22c55e" />
            <span style={{ fontSize: '13px', color: '#9ca3af' }}>{match.time}</span>
          </div>
        </div>
      </div>

      {/* Players Progress */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Users size={14} color="#22c55e" />
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>
              {match.current_players}/{match.max_players} joueurs
            </span>
          </div>
          {isFull && !isCompleted && (
            <span style={{ fontSize: '10px', fontWeight: 500, color: '#f87171', backgroundColor: 'rgba(248,113,113,0.1)', padding: '2px 8px', borderRadius: '9999px' }}>
              Complet
            </span>
          )}
        </div>
        <Progress value={progressPercent} className="h-1.5" style={{ backgroundColor: '#222' }} indicatorClassName="bg-[#22c55e]" />
      </div>

      {/* Price & Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#22c55e' }}>{match.price}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isOrganizer && (
            <button
              data-testid={`delete-match-${match.id}`}
              onClick={handleDelete}
              aria-label="Supprimer le match"
              style={{
                height: '32px', width: '32px', borderRadius: '8px',
                backgroundColor: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.4)',
                color: '#ef4444',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
              className="hover:bg-[rgba(239,68,68,0.18)]"
            >
              <Trash2 size={14} />
            </button>
          )}
          <button
            data-testid={`share-match-${match.id}`}
            onClick={handleShare}
            style={{ height: '32px', padding: '0 12px', borderRadius: '8px', backgroundColor: 'transparent', border: '1px solid #333', color: '#9ca3af', cursor: 'pointer' }}
            className="hover:bg-[#222]"
          >
            <Share2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
